/**
 * Meetings Page Types
 *
 * TypeScript interfaces and Zod schemas for the redesigned Meetings Page.
 * Supports side-by-side Agenda + Calendar layout with Meeting Detail Modal.
 *
 * Related Stories: 3.7 (Meetings Page), 3.8 (Pre-Meeting Preparation)
 */
import { z } from 'zod';
import { ATTENDEE_RESEARCH_MAX_LIST_ITEMS } from './ai-model-config.js';
import { DEAL_MATCH_STRATEGIES } from './deal-match-strategy.js';
import { AttendeeResponseStatus } from './calendar.js';
import { MeetingPlatform } from './meeting-toast.js';
import { MeetingSessionSchema, RecurrenceLabelSchema, } from './meeting-detail.js';
export const AgendaMeetingSchema = z.object({
    id: z.string().uuid(),
    actionItemIds: z.array(z.string().uuid()).optional(),
    userId: z.string().uuid(),
    calendarEventId: z.string().uuid().nullable(),
    sourceStatus: z.enum(['active', 'deleted']),
    sourceDeletedAt: z.string().datetime().nullable(),
    title: z.string(),
    startedAt: z.string().datetime(),
    endedAt: z.string().datetime().nullable(),
    durationMs: z.number().nullable(),
    scheduledStart: z.string().nullable(),
    scheduledEnd: z.string().nullable(),
    sessionCount: z.number().int().min(0),
    sessions: z.array(MeetingSessionSchema),
    status: z.string(),
    meetingUrl: z.string().url().nullable(),
    meetingPlatform: z.enum(Object.values(MeetingPlatform)).nullable(),
    clientId: z.string().uuid().nullable(),
    clientName: z.string().nullable(),
    clientLogoUrl: z.string().url().nullable(),
    dealStage: z.string().nullable(),
    dealName: z.string().nullable(),
    dealMatchStrategy: z.enum(DEAL_MATCH_STRATEGIES).nullable().optional(),
    dealConfidenceScore: z.number().min(0).max(100).nullable().optional(),
    dealLinkReason: z.string().nullable().optional(),
    primaryAttendee: z
        .object({
        name: z.string(),
        email: z.string().email(),
        title: z.string().nullable(),
        company: z.string().nullable(),
        avatarUrl: z.string().url().nullable(),
    })
        .nullable(),
    attendees: z.array(z.object({
        name: z.string(),
        email: z.string(),
        avatarUrl: z.string().nullable(),
    })).optional(),
    summarySnippet: z.string().nullable(),
    isRecurring: z.boolean().default(false),
    recurringEventId: z.string().nullable(),
    recurrenceLabel: RecurrenceLabelSchema.nullable(),
    seriesSequence: z.number().int().nullable(),
    seriesTotal: z.number().int().nullable(),
    seriesFirstStart: z.string().nullable(),
    isAllDay: z.boolean().default(false),
});
export const AttendeeResearchSchema = z.object({
    email: z.string().email(),
    name: z.string().nullable(),
    currentTitle: z.string().nullable(),
    currentCompany: z.string().nullable(),
    linkedinUrl: z.string().url().nullable(),
    linkedinSummary: z.string().nullable(),
    shortSummary: z.string().nullable().optional(),
    companyBackground: z.string().nullable(),
    recentNews: z.array(z.string()).max(ATTENDEE_RESEARCH_MAX_LIST_ITEMS),
    interests: z.array(z.string()).max(ATTENDEE_RESEARCH_MAX_LIST_ITEMS),
    potentialTalkingPoints: z.array(z.string()).max(ATTENDEE_RESEARCH_MAX_LIST_ITEMS),
    researchedAt: z.string().datetime(),
});
// =============================================================================
// AI Chat Types
// =============================================================================
/**
 * Chat message role
 */
export const ChatRole = {
    USER: 'user',
    ASSISTANT: 'assistant',
};
export const MeetingChatRequestSchema = z.object({
    meetingId: z.string().uuid(),
    message: z.string().min(1).max(2000),
});
export const GenerateFollowUpRequestSchema = z.object({
    meetingId: z.string().uuid(),
    tone: z.enum(['formal', 'friendly', 'concise']).optional(),
    includeActionItems: z.boolean().optional(),
    includeNextSteps: z.boolean().optional(),
});
export const AgendaQuerySchema = z.object({
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    limit: z.number().min(1).max(100).optional(),
    cursor: z.string().optional(),
});
// =============================================================================
// Today's Queue Types (Story 4.3)
// =============================================================================
/**
 * Meeting status for Today's Queue
 */
export const TodaysMeetingStatus = {
    LIVE: 'live',
    UPCOMING: 'upcoming',
    COMPLETED: 'completed',
};
export const TodaysMeetingSchema = z.object({
    id: z.string().uuid(),
    actionItemIds: z.array(z.string().uuid()).optional(),
    calendarEventId: z.string().uuid().nullable(),
    sourceStatus: z.enum(['active', 'deleted']),
    sourceDeletedAt: z.string().datetime().nullable(),
    title: z.string(),
    startTime: z.string().datetime(),
    endTime: z.string().datetime().nullable(),
    scheduledStart: z.string().nullable(),
    scheduledEnd: z.string().nullable(),
    status: z.enum(Object.values(TodaysMeetingStatus)),
    meetingUrl: z.string().url().nullable(),
    platform: z.enum(['zoom', 'google_meet', 'teams', 'other']).nullable(),
    organizerEmail: z.string().email().nullable().optional(),
    clientId: z.string().uuid().nullable(),
    clientName: z.string().nullable(),
    clientLogoUrl: z.string().url().nullable(),
    dealId: z.string().uuid().nullable(),
    dealName: z.string().nullable(),
    dealStage: z.string().nullable(),
    dealMatchStrategy: z.enum(DEAL_MATCH_STRATEGIES).nullable().optional(),
    dealConfidenceScore: z.number().min(0).max(100).nullable().optional(),
    dealLinkReason: z.string().nullable().optional(),
    primaryAttendee: z
        .object({
        name: z.string(),
        email: z.string().email(),
        avatarUrl: z.string().url().nullable(),
    })
        .nullable(),
    attendees: z.array(z.object({
        name: z.string(),
        email: z.string(),
        avatarUrl: z.string().nullable(),
        role: z.string().optional(),
        isAppUser: z.boolean().optional(),
        appUserId: z.string().uuid().optional(),
        appUserRole: z.string().optional(),
        responseStatus: z.enum(Object.values(AttendeeResponseStatus)).optional(),
        isOrganizer: z.boolean().optional(),
    })).optional(),
    canModifyEvent: z.boolean().optional(),
    durationSeconds: z.number().nullable(),
    sessions: z.array(MeetingSessionSchema),
    isLinked: z.boolean(),
    isCalendarEvent: z.boolean().optional(),
    userId: z.string().uuid().optional(),
    userName: z.string().optional(),
    userAvatarUrl: z.string().url().nullable().optional(),
    userRole: z.string().optional(),
    meetingOwnerIds: z.array(z.string().uuid()).optional(),
    isRecurring: z.boolean().default(false),
    recurringEventId: z.string().nullable(),
    recurrenceLabel: RecurrenceLabelSchema.nullable(),
    seriesSequence: z.number().int().nullable(),
    seriesTotal: z.number().int().nullable(),
    seriesFirstStart: z.string().nullable(),
    isAllDay: z.boolean().default(false),
});
export const TodaysQueueResponseSchema = z.object({
    meetings: z.array(TodaysMeetingSchema),
    stats: z.object({
        total: z.number(),
        live: z.number(),
        upcoming: z.number(),
        completed: z.number(),
    }),
    nextCursor: z.string().nullable().optional(),
    hasMore: z.boolean().optional(),
    searchHitDates: z.array(z.string()).optional(),
});
